const selectors = {
  root: '[data-decision-tree]',
  node: '[data-decision-node]',
  trigger: '[data-node-trigger]',
  overlay: '[data-node-overlay]',
  placeholder: '[data-tree-placeholder]'
};
const DATA_ATTR_ORIGINAL_FILTER_VALUE = 'data-tl-node-original-filter-value';
const DATA_ATTR_ORIGINAL_FILTER_PRIORITY = 'data-tl-node-original-filter-priority';
const DATA_ATTR_ORIGINAL_PLACEHOLDER_FILTER_VALUE = 'data-tl-node-original-placeholder-filter-value';
const DATA_ATTR_ORIGINAL_PLACEHOLDER_FILTER_PRIORITY = 'data-tl-node-original-placeholder-filter-priority';
const isLogEnabled = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  if (typeof window.__DEV_DECISION_TREE_LOG__ !== 'undefined') {
    return Boolean(window.__DEV_DECISION_TREE_LOG__);
  }

  let stored = null;
  try {
    if (typeof window.localStorage !== 'undefined' && window.localStorage !== null) {
      stored = window.localStorage.getItem('tlDecisionTreeDebug');
    }
  } catch (error) {
    if (typeof console !== 'undefined' && typeof console.warn === 'function') {
      console.warn('[DecisionTree] localStorage unavailable', error);
    }
    stored = null;
  }

  if (stored !== null) {
    return stored === 'true';
  }

  return false;
};

const logDebug = (...args) => {
  if (!isLogEnabled() || typeof console === 'undefined') {
    return;
  }

  const logFn = typeof console.debug === 'function' ? console.debug : console.log;
  if (typeof logFn === 'function') {
    logFn(...args);
  }
};

class DecisionTree {
  constructor(root) {
    logDebug('DecisionTree widget initialized', { version: '2025-10-21T' });
    this.root = root;
    this.rootContainer = root.querySelector('[data-root-container]');
    this.treeDataUrl = (root.dataset.treeUrl || '').trim();
    this.placeholder = this.rootContainer?.querySelector(selectors.placeholder) || null;
    this.supportedOverlay = root.dataset.supportedOverlay || '';
    this.unsupportedOverlay = root.dataset.unsupportedOverlay || '';
    this.affiliateOverlay = root.dataset.affiliateOverlay || '';
    this.defaultRootKey = root.dataset.defaultRoot || '';
    this.affiliateId = (root.dataset.affiliateId || '').trim();
    this.nodeElements = [];
    this.nodesByKey = new Map();
    this.childrenByParent = new Map();
    this.depthByKey = new Map();
    this.levelContainers = new Map();
    this.noscriptElement = root.querySelector('noscript');
    this.compatibilityContainer = root.querySelector('[data-compatible-products]');
    this.remoteCaveatContainer = this.compatibilityContainer?.querySelector('[data-remote-caveat-container]') || null;
    this.floatingFooter = root.querySelector('[data-floating-footer]');
    this.affiliateContainer = root.querySelector('[data-affiliate-container]');
    this.affiliateLink = this.affiliateContainer?.querySelector('[data-affiliate-link]') || null;
    this.unsupportedContainer = root.querySelector('[data-unsupported-container]');
    this.pendingCompatibilityAddToCart = null;
    this.unsubscribeCartUpdate = null;
    this.hasCompatibilityConversion = false;
    this.didSendExitEvent = false;
    this.scrollOffset = this.getScrollOffset();
    this.selectionPath = [];
    this.selectedKey = null;
    this.handleTriggerClick = this.handleTriggerClick.bind(this);
    this.handleCompatibilitySubmit = this.handleCompatibilitySubmit.bind(this);
    this.handleAffiliateLinkClick = this.handleAffiliateLinkClick.bind(this);
    this.handleCartUpdate = this.handleCartUpdate.bind(this);
    this.handlePageHide = this.handlePageHide.bind(this);
    this.selectNodeByKey = this.selectNodeByKey.bind(this);
    this.didInitialize = false;

    if (this.rootContainer) {
      this.levelContainers.set(0, this.rootContainer);
    }

    if (this.treeDataUrl) {
      this.loadExternalTreeData();
    } else {
      this.initialize();
    }
  }

  async loadExternalTreeData() {
    try {
      const payload = await this.fetchTreeData(this.treeDataUrl);
      const nodes = this.normalizeTreeNodes(payload);
      if (nodes.length === 0) {
        this.showPlaceholderMessage('Decision tree data is empty.');
      } else {
        this.renderExternalNodes(nodes);
      }
    } catch (error) {
      console.error('DecisionTree: Failed to load tree data', error);
      this.showPlaceholderMessage('Unable to load decision tree data.');
    } finally {
      this.initialize();
    }
  }

  async fetchTreeData(url) {
    const response = await fetch(url, { credentials: 'same-origin' });
    if (!response.ok) {
      throw new Error(`Tree data request failed with status ${response.status}`);
    }
    const contentType = response.headers.get('content-type') || '';
    if (!contentType.includes('application/json') && !contentType.includes('+json')) {
      console.warn('DecisionTree: Expected JSON response but received', contentType);
    }
    return response.json();
  }

  normalizeTreeNodes(data) {
    if (!data) {
      return [];
    }

    if (Array.isArray(data)) {
      return data;
    }

    if (Array.isArray(data.nodes)) {
      return data.nodes;
    }

    return [];
  }

  renderExternalNodes(nodes) {
    if (!this.rootContainer) {
      return;
    }

    const fragment = document.createDocumentFragment();
    nodes.forEach((nodeData) => {
      const nodeElement = this.createNodeElement(nodeData);
      if (nodeElement) {
        fragment.appendChild(nodeElement);
      }
    });

    this.rootContainer.innerHTML = '';
    this.rootContainer.appendChild(fragment);
    this.placeholder = null;
  }

  createNodeElement(nodeData) {
    if (!nodeData || typeof nodeData !== 'object') {
      return null;
    }

    const key = this.normalizeString(nodeData.node_key || nodeData.key || nodeData.id);
    if (!key) {
      return null;
    }

    const parentKey = this.normalizeString(nodeData.parent_node_key || nodeData.parentKey || '');
    const label = this.normalizeString(nodeData.label || nodeData.title || '');
    const branchHeading = this.normalizeString(nodeData.branch_heading || nodeData.branchHeading || '');
    let supported = this.normalizeBoolean(nodeData.supported);
    let affiliate = this.normalizeBoolean(nodeData.affiliate);
    const affiliateUrl = this.normalizeString(nodeData.affiliate_url || nodeData.affiliateUrl || '');
    const imageSource = this.normalizeString(nodeData.image || nodeData.image_url || '');
    const imageAlt =
      this.normalizeString(nodeData.image_alt || nodeData.imageAlt || '') ||
      (label ? label : 'Decision tree option');

    if (affiliate && !affiliateUrl) {
      affiliate = false;
    }
    if (affiliate) {
      supported = false;
    }

    const node = document.createElement('div');
    node.className = 'tl-decision-tree__node';
    node.setAttribute('data-decision-node', '');
    node.dataset.nodeKey = key;
    node.dataset.parentNodeKey = parentKey;
    node.dataset.nodeSupported = supported ? 'true' : 'false';
    node.dataset.nodeAffiliate = affiliate ? 'true' : 'false';
    node.dataset.affiliateUrl = affiliateUrl;
    node.dataset.nodeHeading = branchHeading;
    node.dataset.nodeLabel = label;
    node.dataset.hasChildren = 'false';
    node.dataset.isRoot = parentKey ? 'false' : 'true';
    node.hidden = true;

    const trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.className = 'tl-decision-tree__trigger';
    trigger.setAttribute('data-node-trigger', '');
    const ariaLabel = label || 'Select option';
    trigger.setAttribute('aria-label', ariaLabel);

    const imageWrapper = document.createElement('span');
    imageWrapper.className = 'tl-decision-tree__image-wrapper';
    if (imageSource) {
      const image = document.createElement('img');
      image.className = 'tl-decision-tree__image';
      image.loading = 'lazy';
      image.decoding = 'async';
      image.alt = imageAlt;
      image.src = imageSource;
      imageWrapper.appendChild(image);
      if (this.isOutlineImageSource(imageSource)) {
        imageWrapper.classList.add('tl-decision-tree__image-wrapper--outline');
        node.dataset.tlOutlineNode = 'true';
      }
    } else {
      const placeholder = document.createElement('span');
      placeholder.className = 'tl-decision-tree__placeholder';
      placeholder.setAttribute('aria-hidden', 'true');
      placeholder.textContent = '—';
      imageWrapper.appendChild(placeholder);
    }

    const overlay = document.createElement('span');
    overlay.className = 'tl-decision-tree__overlay';
    overlay.setAttribute('data-node-overlay', '');
    overlay.hidden = true;
    overlay.setAttribute('aria-hidden', 'true');
    imageWrapper.appendChild(overlay);

    trigger.appendChild(imageWrapper);

    if (label) {
      const labelElement = document.createElement('span');
      labelElement.className = 'tl-decision-tree__label';
      labelElement.textContent = label;
      trigger.appendChild(labelElement);
    }

    node.appendChild(trigger);
    return node;
  }

  initialize() {
    if (this.didInitialize) {
      return;
    }

    this.nodeElements = Array.from(this.root.querySelectorAll(selectors.node));
    if (this.nodeElements.length === 0) {
      this.root.dataset.treeReady = 'true';
      this.didInitialize = true;
      return;
    }

    this.nodesByKey.clear();
    this.childrenByParent.clear();
    this.depthByKey.clear();

    this.nodeElements.forEach((node, index) => {
      const key = (node.dataset.nodeKey || '').trim();
      if (!key) {
        return;
      }

      this.nodesByKey.set(key, node);
      node.dataset.sequence = String(index);

      const parentKey = (node.dataset.parentNodeKey || '').trim();
      if (!this.childrenByParent.has(parentKey)) {
        this.childrenByParent.set(parentKey, []);
      }
      this.childrenByParent.get(parentKey).push(key);

      const trigger = node.querySelector(selectors.trigger);
      if (trigger) {
        trigger.addEventListener('click', this.handleTriggerClick);
      }

      this.flagOutlineNode(node);
    });

    this.nodeElements.forEach((node) => {
      const key = (node.dataset.nodeKey || '').trim();
      const depth = this.resolveDepth(key);
      node.dataset.depth = String(depth);

      const hasChildren = (this.childrenByParent.get(key) || []).length > 0;
      node.dataset.hasChildren = hasChildren ? 'true' : 'false';
    });

    if (this.defaultRootKey && this.nodesByKey.has(this.defaultRootKey)) {
      this.selectionPath = this.buildPathForKey(this.defaultRootKey);
      this.selectedKey = this.selectionPath[this.selectionPath.length - 1] || null;
    }

    this.updateUI(this.selectedKey);
    this.bindAnalyticsListeners();
    this.root.dataset.treeReady = 'true';
    this.didInitialize = true;
  }

  bindAnalyticsListeners() {
    if (this.compatibilityContainer) {
      this.compatibilityContainer.addEventListener('submit', this.handleCompatibilitySubmit);
    }

    if (this.affiliateLink) {
      this.affiliateLink.addEventListener('click', this.handleAffiliateLinkClick);
    }

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS?.cartUpdate) {
      this.unsubscribeCartUpdate = subscribe(PUB_SUB_EVENTS.cartUpdate, this.handleCartUpdate);
    }

    if (typeof window !== 'undefined') {
      window.addEventListener('pagehide', this.handlePageHide);
    }
  }

  normalizeString(value) {
    if (typeof value === 'string') {
      return value.trim();
    }
    if (typeof value === 'number') {
      return String(value);
    }
    return '';
  }

  normalizeBoolean(value) {
    if (typeof value === 'boolean') {
      return value;
    }
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      return normalized === 'true' || normalized === '1' || normalized === 'yes' || normalized === 'y';
    }
    if (typeof value === 'number') {
      return value === 1;
    }
    return false;
  }

  showPlaceholderMessage(message) {
    if (!this.rootContainer) {
      return;
    }

    let placeholder = this.rootContainer.querySelector(selectors.placeholder);
    if (!placeholder) {
      placeholder = document.createElement('div');
      placeholder.className = 'tl-decision-tree__placeholder';
      placeholder.setAttribute('data-tree-placeholder', '');
      this.rootContainer.appendChild(placeholder);
    }

    placeholder.hidden = false;
    placeholder.innerHTML = '';
    const paragraph = document.createElement('p');
    paragraph.textContent = message;
    placeholder.appendChild(paragraph);
  }

  selectNodeByKey(key, { emitEvent = false } = {}) {
    if (!key || !this.nodesByKey.has(key)) {
      return;
    }

    const path = this.buildPathForKey(key);
    if (path.length === 0) {
      return;
    }

    // Toggle behavior: clicking the active root collapses back to roots only.
    if (this.selectionPath.length === path.length && this.selectionPath.every((value, index) => value === path[index])) {
      this.clearSelection();
      return;
    }

    this.selectionPath = path;
    this.selectedKey = key;
    this.updateUI(this.selectedKey);

    if (emitEvent) {
      const node = this.nodesByKey.get(key);
      if (node) {
        this.dispatchSelectionEvent(node);
      }
    }
  }

  clearSelection() {
    this.selectionPath = [];
    this.selectedKey = null;
    this.updateUI(this.selectedKey);
  }

  handleTriggerClick(event) {
    event.preventDefault();
    const trigger = event.currentTarget;
    const node = trigger.closest(selectors.node);
    if (!node) {
      return;
    }

    const key = (node.dataset.nodeKey || '').trim();
    if (!key) {
      return;
    }

    const path = this.buildPathForKey(key);
    if (path.length === 0) {
      return;
    }

    const hasChildren = (this.childrenByParent.get(key) || []).length > 0;
    const isAlreadySelected = this.selectedKey === key && this.selectionPath.length === path.length;

    if (isAlreadySelected) {
      this.trackNodeClick(node, path, { hasChildren, interactionType: 'toggle_back' });
      if (hasChildren) {
        this.selectionPath = path.slice(0, -1);
        this.selectedKey = this.selectionPath[this.selectionPath.length - 1] || null;
      } else if (path.length > 1) {
        this.selectionPath = path.slice(0, -1);
        this.selectedKey = this.selectionPath[this.selectionPath.length - 1] || null;
      } else {
        this.clearSelection();
      }
      this.updateUI(this.selectedKey);
      return;
    }

    this.selectionPath = path;
    this.selectedKey = key;
    this.updateUI(this.selectedKey);
    this.trackNodeClick(node, path, { hasChildren, interactionType: 'select' });
    this.dispatchSelectionEvent(node);
  }

  trackNodeClick(node, path, { hasChildren = false, interactionType = 'select' } = {}) {
    if (!node) {
      return;
    }

    const nodeId = (node.dataset.nodeKey || '').trim();
    if (!nodeId) {
      return;
    }

    const nodeLabel = (node.dataset.nodeLabel || '').trim();
    const isSupported = node.dataset.nodeSupported === 'true';
    const isAffiliate = node.dataset.nodeAffiliate === 'true';
    let nodeType = 'question';
    if (!hasChildren) {
      nodeType = isAffiliate ? 'affiliate_result' : (isSupported ? 'supported_result' : 'unsupported_result');
    }

    const normalizedPath = Array.isArray(path) ? path.filter(Boolean) : [];
    const stepIndex = Math.max(1, normalizedPath.length);
    const pathKey = normalizedPath.join('>');

    this.fireGA4Event('compatibility_node_click', {
      node_id: nodeId,
      node_label: nodeLabel || nodeId,
      node_type: nodeType,
      step_index: stepIndex,
      path_key: pathKey,
      page_type: 'compatibility',
      interaction_type: interactionType
    });
  }

  fireGA4Event(eventName, params) {
    if (!eventName || typeof window === 'undefined' || typeof window.gtag !== 'function') {
      return;
    }

    window.gtag('event', eventName, params);
  }

  getSelectionAnalyticsContext() {
    const selectedNode = this.selectedKey ? this.nodesByKey.get(this.selectedKey) : null;
    const nodeId = selectedNode ? (selectedNode.dataset.nodeKey || '').trim() : '';
    const nodeLabel = selectedNode ? (selectedNode.dataset.nodeLabel || '').trim() : '';
    const path = Array.isArray(this.selectionPath) ? this.selectionPath.filter(Boolean) : [];

    return {
      node_id: nodeId || null,
      node_label: nodeLabel || nodeId || null,
      path_key: path.join('>'),
      step_index: Math.max(1, path.length || 1),
      page_type: 'compatibility'
    };
  }

  handleCompatibilitySubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) {
      return;
    }

    const variantInput = form.querySelector('input[name="id"]');
    const variantId = variantInput ? String(variantInput.value || '').trim() : '';
    if (!variantId) {
      return;
    }

    this.pendingCompatibilityAddToCart = {
      variantId,
      timestamp: Date.now(),
      context: this.getSelectionAnalyticsContext()
    };
  }

  handleCartUpdate(payload = {}) {
    if (!this.pendingCompatibilityAddToCart) {
      return;
    }

    const productVariantId = String(payload.productVariantId || '').trim();
    const source = String(payload.source || '').trim();
    const now = Date.now();
    const isFresh = now - this.pendingCompatibilityAddToCart.timestamp <= 15000;
    const isMatch = isFresh && productVariantId !== '' && productVariantId === this.pendingCompatibilityAddToCart.variantId;

    if (!isMatch) {
      if (!isFresh) {
        this.pendingCompatibilityAddToCart = null;
      }
      return;
    }

    this.hasCompatibilityConversion = true;
    this.fireGA4Event('compatibility_add_to_cart', {
      ...this.pendingCompatibilityAddToCart.context,
      variant_id: productVariantId,
      source: source || 'product-form'
    });
    this.pendingCompatibilityAddToCart = null;
  }

  handleAffiliateLinkClick() {
    const href = this.affiliateLink ? (this.affiliateLink.getAttribute('href') || '').trim() : '';
    if (!href) {
      return;
    }

    this.hasCompatibilityConversion = true;
    const context = this.getSelectionAnalyticsContext();
    this.fireGA4Event('compatibility_referral_click', {
      ...context,
      affiliate_id: this.affiliateId || null,
      destination_url: href
    });
  }

  handlePageHide() {
    if (this.didSendExitEvent || this.hasCompatibilityConversion) {
      return;
    }

    this.didSendExitEvent = true;
    const context = this.getSelectionAnalyticsContext();
    this.fireGA4Event('compatibility_exit', {
      ...context,
      exited_without_conversion: true,
      interacted_with_tree: this.selectionPath.length > 0
    });
  }

  buildPathForKey(key) {
    const path = [];
    let currentKey = key;
    const safetySet = new Set();

    while (currentKey) {
      if (safetySet.has(currentKey)) {
        console.warn(`DecisionTree: detected circular reference at "${currentKey}".`);
        break;
      }
      safetySet.add(currentKey);

      const node = this.nodesByKey.get(currentKey);
      if (!node) {
        break;
      }

      path.unshift(currentKey);
      const parentKey = (node.dataset.parentNodeKey || '').trim();
      if (!parentKey || !this.nodesByKey.has(parentKey)) {
        break;
      }
      currentKey = parentKey;
    }

    return path;
  }

  resolveDepth(key, visiting = new Set()) {
    if (this.depthByKey.has(key)) {
      return this.depthByKey.get(key);
    }

    if (!key || !this.nodesByKey.has(key)) {
      this.depthByKey.set(key, 0);
      return 0;
    }

    if (visiting.has(key)) {
      console.warn(`DecisionTree: depth resolution loop detected at "${key}".`);
      this.depthByKey.set(key, 0);
      return 0;
    }

    visiting.add(key);
    const node = this.nodesByKey.get(key);
    const parentKey = (node.dataset.parentNodeKey || '').trim();
    let depth = 0;

    if (parentKey) {
      if (this.nodesByKey.has(parentKey)) {
        depth = this.resolveDepth(parentKey, visiting) + 1;
      } else {
        console.warn(`DecisionTree: parent "${parentKey}" not found for node "${key}".`);
        depth = 1;
      }
    }

    visiting.delete(key);
    this.depthByKey.set(key, depth);
    return depth;
  }

  updateUI(selectedKey) {
    const pathSet = new Set(this.selectionPath);
    const selectedNode = this.nodesByKey.get(selectedKey) || null;
    const selectedChildren = selectedNode ? this.childrenByParent.get(selectedKey) || [] : [];
    const selectedIsLeaf = selectedNode ? selectedChildren.length === 0 : false;
    const selectedIsSupported = selectedNode ? selectedNode.dataset.nodeSupported === 'true' : false;
    const selectedIsAffiliate = selectedNode ? selectedNode.dataset.nodeAffiliate === 'true' : false;
    const affiliateUrl = selectedNode && selectedIsAffiliate ? selectedNode.dataset.affiliateUrl || '' : '';
    const shouldShowCompatibility =
      Boolean(this.compatibilityContainer) && selectedIsLeaf && selectedIsSupported && !selectedIsAffiliate;
    const shouldShowRemoteCaveat = shouldShowCompatibility && this.isSelectionUnderRemoteRoot();
    const shouldShowAffiliate = selectedIsLeaf && selectedIsAffiliate && affiliateUrl !== '';
    const shouldShowUnsupported =
      selectedIsLeaf && !selectedIsSupported && !selectedIsAffiliate && Boolean(this.unsupportedContainer);

    this.nodeElements.forEach((node) => {
      const key = (node.dataset.nodeKey || '').trim();
      if (!key) {
        return;
      }

      const depth = Number(node.dataset.depth || 0);
      const parentKey = (node.dataset.parentNodeKey || '').trim();
      const isRoot = parentKey === '';
      const isInPath = pathSet.has(key);
      const isSelected = key === selectedKey;
      const hasChildren = (this.childrenByParent.get(key) || []).length > 0;
      const shouldShow = isRoot || pathSet.has(parentKey) || pathSet.has(key);
      if (typeof window !== 'undefined' && window.console && window.__DEV_DECISION_TREE_LOG__) {
        logDebug('DecisionTree update', {
          key,
          depth,
          parentKey,
          isRoot,
          isInPath,
          isSelected,
          hasChildren,
          shouldShow
        });
      }

      const container = this.ensureLevelContainer(depth);
      if (container && node.parentElement !== container) {
        container.appendChild(node);
      }

      if (shouldShow) {
        node.hidden = false;
        node.style.display = '';
      } else {
        node.hidden = true;
        node.style.display = 'none';
      }
      node.classList.toggle('is-path', isInPath);
      node.classList.toggle('is-selected', isSelected);

      const trigger = node.querySelector(selectors.trigger);
      if (trigger) {
        if (hasChildren) {
          trigger.setAttribute('aria-expanded', isInPath ? 'true' : 'false');
        } else {
          trigger.removeAttribute('aria-expanded');
        }
      }

      const shouldShowNodeOverlay = isSelected || isInPath;
      if (shouldShowNodeOverlay) {
        const forceSupported = !isSelected || (isSelected && !selectedIsLeaf);
        this.showOverlay(node, { forceSupported });
      } else {
        this.clearOverlay(node);
      }
    });

    this.levelContainers.forEach((container, level) => {
      if (!container) {
        return;
      }

      const headingEl = container.querySelector('[data-level-heading]');
      if (!headingEl) {
        return;
      }

      if (level === 0) {
        headingEl.hidden = true;
        return;
      }

      const parentKey = this.selectionPath[level - 1];
      if (!parentKey) {
        headingEl.hidden = true;
        return;
      }

      const parentNode = this.nodesByKey.get(parentKey);
      if (!parentNode) {
        headingEl.hidden = true;
        return;
      }

      const headingText = (parentNode.dataset.nodeHeading || parentNode.dataset.nodeLabel || '').trim();

      if (headingText) {
        headingEl.textContent = headingText;
        headingEl.hidden = false;
      } else {
        headingEl.hidden = true;
      }

      if (isLogEnabled()) {
        logDebug('[DecisionTree] Level heading update', { level, parentKey, headingText, hidden: headingEl.hidden });
      }
    });

    this.orderLevelContainers();
    this.updateLevelHeadings();
    this.updateLevelVisibility();
    this.toggleCompatibleProducts(shouldShowCompatibility);
    this.toggleRemoteCaveatMessage(shouldShowRemoteCaveat);
    this.toggleAffiliateButton(shouldShowAffiliate, affiliateUrl);
    this.toggleUnsupportedMessage(shouldShowUnsupported);
    this.toggleFloatingFooter(true);
    this.scheduleScroll({
      selectedNode,
      selectedIsLeaf,
      shouldShowAffiliate,
      shouldShowUnsupported,
      selectedLevel: selectedNode ? Number(selectedNode.dataset.depth || 0) : null,
      selectionPath: this.selectionPath
    });

  }

  showOverlay(node, options = {}) {
    const overlay = node.querySelector(selectors.overlay);
    if (!overlay) {
      return;
    }

    const { forceSupported = false } = options;
    const isAffiliate = node.dataset.nodeAffiliate === 'true';
    const isSupported = node.dataset.nodeSupported === 'true';
    const nodeKey = (node.dataset.nodeKey || '').trim();

    let overlayUrl = '';
    if (forceSupported && this.supportedOverlay) {
      overlayUrl = this.supportedOverlay;
    } else if (isAffiliate && this.affiliateOverlay) {
      overlayUrl = this.affiliateOverlay;
    } else if (isSupported && this.supportedOverlay) {
      overlayUrl = this.supportedOverlay;
    } else if (!isSupported && !isAffiliate && this.unsupportedOverlay) {
      overlayUrl = this.unsupportedOverlay;
    }

    if (!overlayUrl) {
      overlay.hidden = true;
      overlay.classList.remove('is-visible');
      node.classList.remove('is-overlay-visible');
      if (isLogEnabled()) {
        logDebug('[DecisionTree] Overlay skip', {
          key: nodeKey,
          forceSupported,
          isAffiliate,
          isSupported
        });
      }
      this.resetNodeMediaFilter(node);
      return;
    }

    let overlayImage = overlay.querySelector('img');
    if (!overlayImage) {
      overlayImage = document.createElement('img');
      overlayImage.alt = '';
      overlayImage.decoding = 'async';
      overlayImage.loading = 'lazy';
      overlay.appendChild(overlayImage);
    }

    if (overlayImage.src !== overlayUrl) {
      overlayImage.src = overlayUrl;
    }

    overlay.hidden = false;
    overlay.classList.add('is-visible');
    node.classList.add('is-overlay-visible');
    this.applyNodeMediaDarkening(node);

    if (isLogEnabled()) {
      let filterValue = 'unknown';
      let placeholderFilterValue = 'n/a';
      const imageElement = node.querySelector('.tl-decision-tree__image');
      const placeholderElement = node.querySelector('.tl-decision-tree__image-wrapper .tl-decision-tree__placeholder');
      if (imageElement && typeof window !== 'undefined' && typeof window.getComputedStyle === 'function') {
        filterValue = window.getComputedStyle(imageElement).filter || 'none';
      }
      if (placeholderElement && typeof window !== 'undefined' && typeof window.getComputedStyle === 'function') {
        placeholderFilterValue = window.getComputedStyle(placeholderElement).filter || 'none';
      }

      logDebug('[DecisionTree] Overlay applied', {
        key: nodeKey,
        forceSupported,
        isAffiliate,
        isSupported,
        overlayUrl,
        classList: [...node.classList],
        filter: filterValue,
        placeholderFilter: placeholderFilterValue
      });
    }
  }

  clearOverlay(node) {
    if (!node) {
      return;
    }

    const overlay = node.querySelector(selectors.overlay);
    if (overlay) {
      overlay.hidden = true;
      overlay.classList.remove('is-visible');
      const image = overlay.querySelector('img');
      if (image) {
        image.removeAttribute('src');
      }
    }
    node.classList.remove('is-overlay-visible');
    this.resetNodeMediaFilter(node);

    if (isLogEnabled()) {
      const key = (node.dataset.nodeKey || '').trim();
      logDebug('[DecisionTree] Overlay cleared', {
        key,
        hasOverlayImage: Boolean(node.querySelector(`${selectors.overlay} img`))
      });
    }
  }

  applyNodeMediaDarkening(node) {
    if (!node) {
      return;
    }

    const imageElement = node.querySelector('.tl-decision-tree__image');
    if (imageElement) {
      if (!imageElement.hasAttribute(DATA_ATTR_ORIGINAL_FILTER_VALUE)) {
        imageElement.setAttribute(DATA_ATTR_ORIGINAL_FILTER_VALUE, imageElement.style.getPropertyValue('filter') || '');
        imageElement.setAttribute(
          DATA_ATTR_ORIGINAL_FILTER_PRIORITY,
          imageElement.style.getPropertyPriority('filter') || ''
        );
      }
      imageElement.style.setProperty('filter', 'brightness(0.55)', 'important');
    }

    const placeholderElement = node.querySelector('.tl-decision-tree__image-wrapper .tl-decision-tree__placeholder');
    if (placeholderElement) {
      if (!placeholderElement.hasAttribute(DATA_ATTR_ORIGINAL_PLACEHOLDER_FILTER_VALUE)) {
        placeholderElement.setAttribute(
          DATA_ATTR_ORIGINAL_PLACEHOLDER_FILTER_VALUE,
          placeholderElement.style.getPropertyValue('filter') || ''
        );
        placeholderElement.setAttribute(
          DATA_ATTR_ORIGINAL_PLACEHOLDER_FILTER_PRIORITY,
          placeholderElement.style.getPropertyPriority('filter') || ''
        );
      }
      placeholderElement.style.setProperty('filter', 'brightness(0.55)', 'important');
    }
  }

  resetNodeMediaFilter(node) {
    if (!node) {
      return;
    }

    const imageElement = node.querySelector('.tl-decision-tree__image');
    if (imageElement && imageElement.hasAttribute(DATA_ATTR_ORIGINAL_FILTER_VALUE)) {
      const originalFilter = imageElement.getAttribute(DATA_ATTR_ORIGINAL_FILTER_VALUE) || '';
      const originalPriority = imageElement.getAttribute(DATA_ATTR_ORIGINAL_FILTER_PRIORITY) || '';
      if (originalFilter) {
        imageElement.style.setProperty(
          'filter',
          originalFilter,
          originalPriority === 'important' ? 'important' : ''
        );
      } else {
        imageElement.style.removeProperty('filter');
      }
      imageElement.removeAttribute(DATA_ATTR_ORIGINAL_FILTER_VALUE);
      imageElement.removeAttribute(DATA_ATTR_ORIGINAL_FILTER_PRIORITY);
    }

    const placeholderElement = node.querySelector('.tl-decision-tree__image-wrapper .tl-decision-tree__placeholder');
    if (placeholderElement && placeholderElement.hasAttribute(DATA_ATTR_ORIGINAL_PLACEHOLDER_FILTER_VALUE)) {
      const originalFilter = placeholderElement.getAttribute(DATA_ATTR_ORIGINAL_PLACEHOLDER_FILTER_VALUE) || '';
      const originalPriority = placeholderElement.getAttribute(DATA_ATTR_ORIGINAL_PLACEHOLDER_FILTER_PRIORITY) || '';
      if (originalFilter) {
        placeholderElement.style.setProperty(
          'filter',
          originalFilter,
          originalPriority === 'important' ? 'important' : ''
        );
      } else {
        placeholderElement.style.removeProperty('filter');
      }
      placeholderElement.removeAttribute(DATA_ATTR_ORIGINAL_PLACEHOLDER_FILTER_VALUE);
      placeholderElement.removeAttribute(DATA_ATTR_ORIGINAL_PLACEHOLDER_FILTER_PRIORITY);
    }
  }

  flagOutlineNode(node) {
    if (!node) {
      return;
    }

    const imageWrapper = node.querySelector('.tl-decision-tree__image-wrapper');
    if (!imageWrapper) {
      return;
    }

    const image = imageWrapper.querySelector('.tl-decision-tree__image');
    if (!image) {
      return;
    }

    const candidates = [
      image.currentSrc,
      image.getAttribute('src'),
      image.getAttribute('data-src'),
      image.getAttribute('data-original-src')
    ].filter(Boolean);

    const hasOutline = candidates.some((src) => this.isOutlineImageSource(src));
    if (hasOutline) {
      imageWrapper.classList.add('tl-decision-tree__image-wrapper--outline');
      node.dataset.tlOutlineNode = 'true';
    }

    if (!image.dataset.tlOutlineListener) {
      image.addEventListener('load', () => {
        this.flagOutlineNode(node);
      });
      image.dataset.tlOutlineListener = 'true';
    }
  }

  isOutlineImageSource(src) {
    if (typeof src !== 'string') {
      return false;
    }
    return src.toLowerCase().includes('outline');
  }

  dispatchSelectionEvent(node) {
    const key = (node.dataset.nodeKey || '').trim();
    const detail = {
      key: key || null,
      parentKey: (node.dataset.parentNodeKey || '').trim() || null,
      level: Number(node.dataset.depth || 0),
      supported: node.dataset.nodeSupported === 'true',
      isLeaf: (this.childrenByParent.get(key) || []).length === 0,
      path: [...this.selectionPath]
    };

    const event = new CustomEvent('decisionTree:nodeSelected', {
      bubbles: true,
      detail
    });

    this.root.dispatchEvent(event);
  }

  ensureLevelContainer(level) {
    const normalizedLevel = Number(level) || 0;
    if (this.levelContainers.has(normalizedLevel)) {
      return this.levelContainers.get(normalizedLevel);
    }

    const container = document.createElement('div');
    container.className = `tl-decision-tree__level tl-decision-tree__level--${normalizedLevel}`;
    container.dataset.level = String(normalizedLevel);
    container.hidden = normalizedLevel !== 0;

    if (this.noscriptElement) {
      this.root.insertBefore(container, this.noscriptElement);
    } else {
      this.root.appendChild(container);
    }

    this.levelContainers.set(normalizedLevel, container);
    this.orderLevelContainers();
    return container;
  }

  orderLevelContainers() {
    const orderedLevels = Array.from(this.levelContainers.entries())
      .filter(([, container]) => container)
      .sort(([a], [b]) => a - b);

    if (orderedLevels.length === 0) {
      return;
    }

    orderedLevels.forEach(([, container], index) => {
      container.setAttribute('data-level', String(index));
      if (index > 0 && !container.querySelector('[data-level-heading]')) {
        const headingEl = document.createElement('h3');
        headingEl.className = 'tl-decision-tree__level-heading';
        headingEl.setAttribute('data-level-heading', '');
        headingEl.hidden = true;
        container.insertBefore(headingEl, container.firstChild);
      }
    });

    const fragment = document.createDocumentFragment();
    orderedLevels.forEach(([, container]) => {
      fragment.appendChild(container);
    });

    if (this.noscriptElement) {
      this.root.insertBefore(fragment, this.noscriptElement);
    } else {
      this.root.appendChild(fragment);
    }
  }

  toggleCompatibleProducts(shouldShow) {
    if (!this.compatibilityContainer) {
      return;
    }

    if (shouldShow) {
      this.compatibilityContainer.hidden = false;
    } else {
      this.compatibilityContainer.hidden = true;
    }
  }

  getScrollOffset() {
    const header = document.querySelector('.shopify-section-header-sticky, .section-header');
    if (header && header.offsetHeight) {
      return header.offsetHeight + 16;
    }
    return 80;
  }

  scrollToTarget(target) {
    if (!target) {
      return;
    }

    const rect = target.getBoundingClientRect();
    const absoluteTop = rect.top + window.pageYOffset;
    window.scrollTo({
      top: Math.max(absoluteTop - this.scrollOffset, 0),
      behavior: 'smooth'
    });
  }

  scheduleScroll({ selectedNode, selectedIsLeaf, shouldShowAffiliate, shouldShowUnsupported, selectedLevel, selectionPath }) {
    const leafDelay = shouldShowAffiliate || shouldShowUnsupported ? 400 : 0;

    const scrollFn = () => {
      const target = this.resolveScrollTarget({
        selectedNode,
        selectedIsLeaf,
        selectedLevel,
        selectionPath
      });
      if (target) {
        this.scrollToTarget(target);
      }
    };

    if (selectedIsLeaf) {
      setTimeout(scrollFn, leafDelay);
    } else {
      scrollFn();
    }
  }

  resolveScrollTarget({ selectedNode, selectedIsLeaf, selectedLevel, selectionPath }) {
    if (!selectionPath || selectionPath.length === 0) {
      return this.root;
    }

    if (selectedIsLeaf) {
      const leafTarget = this.getLeafContentTarget();
      if (leafTarget) {
        return leafTarget;
      }
    }

    if (selectedNode && typeof selectedLevel === 'number') {
      const nextLevel = selectedLevel + 1;
      const nextContainer = this.levelContainers.get(nextLevel);
      if (nextContainer && !nextContainer.hidden) {
        const heading = nextContainer.querySelector('[data-level-heading]');
        if (heading && !heading.hidden) {
          return heading;
        }
        const firstVisibleChild = Array.from(nextContainer.children).find(
          (child) => child.matches(selectors.node) && !child.hidden
        );
        if (firstVisibleChild) {
          return firstVisibleChild;
        }
        return nextContainer;
      }
    }

    if (selectionPath.length > 1) {
      const parentKey = selectionPath[selectionPath.length - 2];
      const parentNode = this.nodesByKey.get(parentKey);
      if (parentNode) {
        return parentNode;
      }
    }

    return selectedNode || this.root;
  }

  getLeafContentTarget() {
    if (this.compatibilityContainer && !this.compatibilityContainer.hidden) {
      return this.compatibilityContainer;
    }
    if (this.affiliateContainer && !this.affiliateContainer.hidden) {
      return this.affiliateContainer;
    }
    if (this.unsupportedContainer && !this.unsupportedContainer.hidden) {
      return this.unsupportedContainer;
    }
    return null;
  }

  updateLevelHeadings() {
    this.levelContainers.forEach((container, level) => {
      if (!container || level === 0) {
        const headingEl = container?.querySelector('[data-level-heading]');
        if (headingEl) {
          headingEl.hidden = true;
        }
        return;
      }

      const headingEl = container.querySelector('[data-level-heading]');
      if (!headingEl) {
        return;
      }

      const parentKey = this.selectionPath[level - 1];
      if (!parentKey) {
        headingEl.hidden = true;
        return;
      }

      const parentNode = this.nodesByKey.get(parentKey);
      const headingText = ((parentNode?.dataset.nodeHeading || parentNode?.dataset.nodeLabel) || '').trim();
      logDebug('[DecisionTree] Level heading update', { level, parentKey, headingText });
      if (headingText) {
        headingEl.textContent = headingText;
        headingEl.hidden = false;
      } else {
        headingEl.hidden = true;
      }
    });
  }

  toggleAffiliateButton(shouldShow, url) {
    if (!this.affiliateContainer) {
      return;
    }

    if (shouldShow && url) {
      this.affiliateContainer.hidden = false;
      if (this.affiliateLink) {
        const finalUrl = this.affiliateId ? `${url}${this.affiliateId}` : url;
        this.affiliateLink.href = finalUrl;
      }
    } else {
      this.affiliateContainer.hidden = true;
      if (this.affiliateLink) {
        this.affiliateLink.removeAttribute('href');
      }
    }
  }

  toggleUnsupportedMessage(shouldShow) {
    if (!this.unsupportedContainer) {
      return;
    }

    this.unsupportedContainer.hidden = !shouldShow;
  }

  toggleFloatingFooter(shouldShow) {
    if (!this.floatingFooter) {
      return;
    }

    this.floatingFooter.hidden = !shouldShow;
  }

  isSelectionUnderRemoteRoot() {
    if (this.selectionPath.length === 0) {
      return false;
    }

    const rootNodeKey = this.selectionPath[0];
    if (!rootNodeKey) {
      return false;
    }

    const rootNode = this.nodesByKey.get(rootNodeKey);
    if (!rootNode) {
      return false;
    }

    const normalizedRootLabel = ((rootNode.dataset.nodeLabel || '').trim() || '').toLowerCase();
    const normalizedRootHeading = ((rootNode.dataset.nodeHeading || '').trim() || '').toLowerCase();
    const normalizedRootKey = ((rootNode.dataset.nodeKey || '').trim() || '').toLowerCase();

    return normalizedRootLabel === 'remote' || normalizedRootHeading === 'remote' || normalizedRootKey === 'remote';
  }

  toggleRemoteCaveatMessage(shouldShow) {
    if (!this.remoteCaveatContainer) {
      return;
    }

    this.remoteCaveatContainer.hidden = !shouldShow;
  }

  updateLevelVisibility() {
    this.levelContainers.forEach((container, level) => {
      if (level === 0) {
        container.hidden = false;
        return;
      }

      const hasVisibleChild = Array.from(container.children).some((child) => !child.hidden);
      container.hidden = !hasVisibleChild;
    });
  }

  destroy() {
    this.nodeElements.forEach((node) => {
      const trigger = node.querySelector(selectors.trigger);
      if (trigger) {
        trigger.removeEventListener('click', this.handleTriggerClick);
      }
    });

    this.nodesByKey.clear();
    this.childrenByParent.clear();
    this.depthByKey.clear();
    this.levelContainers.clear();
    this.selectionPath = [];
    this.selectedKey = null;
    this.pendingCompatibilityAddToCart = null;
    this.hasCompatibilityConversion = false;
    this.didSendExitEvent = false;

    if (this.compatibilityContainer) {
      this.compatibilityContainer.removeEventListener('submit', this.handleCompatibilitySubmit);
    }

    if (this.affiliateLink) {
      this.affiliateLink.removeEventListener('click', this.handleAffiliateLinkClick);
    }

    if (typeof this.unsubscribeCartUpdate === 'function') {
      this.unsubscribeCartUpdate();
      this.unsubscribeCartUpdate = null;
    }

    if (typeof window !== 'undefined') {
      window.removeEventListener('pagehide', this.handlePageHide);
    }
  }
}

const instances = new WeakMap();

function init(root) {
  if (!root || instances.has(root)) {
    return;
  }

  const instance = new DecisionTree(root);
  instances.set(root, instance);
}

function loadAll() {
  document.querySelectorAll(selectors.root).forEach((root) => init(root));
}

function destroy(root) {
  const instance = instances.get(root);
  if (!instance) {
    return;
  }

  instance.destroy();
  instances.delete(root);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', loadAll);
} else {
  loadAll();
}

document.addEventListener('shopify:section:load', (event) => {
  const root = event.target.querySelector(selectors.root);
  init(root);
});

document.addEventListener('shopify:section:unload', (event) => {
  const root = event.target.querySelector(selectors.root);
  destroy(root);
});

document.addEventListener('shopify:block:select', (event) => {
  const nodeElement = event.target.closest(selectors.node);
  const root = event.target.closest(selectors.root);

  if (!root) {
    return;
  }

  const instance = instances.get(root);
  if (!instance) {
    return;
  }

  if (nodeElement) {
    const nodeKey = (nodeElement.dataset.nodeKey || '').trim();
    instance.selectNodeByKey(nodeKey);
    nodeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
  } else {
    instance.clearSelection();
  }
});

document.addEventListener('shopify:block:deselect', (event) => {
  const root = event.target.closest(selectors.root);
  if (!root) {
    return;
  }

  const instance = instances.get(root);
  if (instance) {
    instance.clearSelection();
  }
});
